/**
 * Whether the call log is drawn threaded, remembered per browser.
 *
 * A habit rather than a decision, like the density toggle next door: a desk
 * that rings the same twenty customers all week wants the folded list every
 * morning, and one that takes a hundred calls from a hundred strangers wants
 * the flat one. Re-picking it after every reload is a small daily tax.
 *
 * The CALL screen's setting and nothing else's. The density is shared with the
 * mail screen because the two are the same shape and an operator who works
 * compact works compact; this one is not that. Mail is already threaded by the
 * protocol - a reply carries the id of what it answers - while a call thread is
 * only ever a guess from a phone number, so there is nothing here for the mail
 * screen to share.
 *
 * Plain `useState` over a module store, unlike `density.ts`: this preference has
 * exactly one reader, the list pane. A module store buys reach across the tree,
 * and there is no tree to reach across.
 */
import { useCallback, useState } from "react";

const KEY = "crm.calls.threaded";

/** What this browser last left on. Never throws - a list that failed to render
 *  because a stored preference was malformed would be far worse than a toggle
 *  that starts in the wrong position. */
function read(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** The setting plus the switch, for the pane that owns the toggle. */
export function useThreadedCalls(): [boolean, () => void] {
  const [on, setOn] = useState<boolean>(read);

  const toggle = useCallback(() => {
    setOn((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(KEY, next ? "1" : "0");
      } catch {
        // A browser with storage switched off still gets the toggle; it simply
        // forgets it on the next reload.
      }
      return next;
    });
  }, []);

  return [on, toggle];
}
